/**
 * Prefetch hooks — thin adapters over the data plane.
 *
 * All scheduling, rate limiting and staleness decisions live in
 * `@/lib/queries/data-plane`; these hooks only translate DOM intent
 * (pointerdown, intersection) into a plane call with the right priority.
 */
import { useCallback, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { prefetch, type PlaneQuery } from '@/lib/queries/data-plane';

/** Query shape the surfaces pass around (the factories in
 *  `@/lib/queries/detail` return exactly this). */
export interface DetailQuery {
  queryKey: readonly unknown[];
  queryFn: (context: { pageParam: unknown }) => Promise<unknown>;
  infinite?: boolean;
  initialPageParam?: unknown;
}

/**
 * INTENT — fire immediately. Used for pointerdown, where the user has
 * signalled an imminent navigation; throttled per key by the plane.
 */
export function prefetchDetailData(
  client: QueryClient,
  q: DetailQuery,
  ttlMs?: number
): Promise<void> {
  return prefetch(client, q as PlaneQuery, 'intent', { ttlMs });
}

export function useDetailPointerPrefetch(q?: DetailQuery): () => void {
  const queryClient = useQueryClient();
  const latestQuery = useRef(q);
  latestQuery.current = q;
  return useCallback(() => {
    if (typeof document === 'undefined' || !latestQuery.current) return;
    void prefetchDetailData(queryClient, latestQuery.current);
  }, [queryClient]);
}

export interface ViewportPrefetchOptions {
  /** Speculative (idle, serialized) rather than intent. Use on surfaces that
   *  prime MANY destinations at once — the profile hub's nav cards. */
  deferToIdle?: boolean;
  ttlMs?: number;
  windowMs?: number;
  maxPerWindow?: number;
}

/**
 * Ref callback that observes `node` and prefetches when it nears the viewport.
 * Settle-once: the observer disconnects on first intersection, because one
 * prefetch per mount is all the plane's TTL needs.
 */
export function useDetailViewportPrefetch(
  kind: string,
  q: DetailQuery | undefined,
  opts?: ViewportPrefetchOptions
): (node: Element | null) => void {
  const queryClient = useQueryClient();
  const latestQuery = useRef(q);
  latestQuery.current = q;
  const deferToIdle = opts?.deferToIdle;
  const ttlMs = opts?.ttlMs;
  const windowMs = opts?.windowMs;
  const maxPerWindow = opts?.maxPerWindow;
  return useCallback(
    (node: Element | null) => {
      if (node === null) return;
      if (typeof document === 'undefined' || typeof IntersectionObserver === 'undefined') return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          if (typeof document === 'undefined' || !latestQuery.current) return;
          // Viewport prefetch is speculative by nature — rate-limited per
          // kind so a fast scroll can't stampede. `deferToIdle` only decides
          // whether it fires now or waits for idle time.
          void prefetch(queryClient, latestQuery.current as PlaneQuery, 'speculative', {
            kind,
            ttlMs,
            windowMs,
            maxPerWindow,
            defer: deferToIdle,
          });
        },
        // Start ~200px before the card paints in: enough head start for the
        // round trip to overlap the scroll, still genuinely "about to be seen".
        { rootMargin: '200px' }
      );
      observer.observe(node);
    },
    [kind, queryClient, deferToIdle, ttlMs, windowMs, maxPerWindow]
  );
}
